import { useContext, useEffect, useMemo } from 'react';
import { FirestoreDB } from '../../utils/firebase/firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
} from 'firebase/firestore';
import { UIContext } from '../../contexts/UIProvider';
import { ACM } from '../../constants/UserRoles';

const UnreadMessageCounts = ({
  unreadCounts,
  setUnreadCounts,
  setUnreadCounts2 = () => {},
  user,
  typeOfUser,
  patientData,
  studentData,
  teacherData,
  teachersChatsForTeachers = [],
  doctorData = [],
  selectedChat, // To identify the currently opened conversation
}) => {
  // const { ACM } = useContext(ACMContext);
  const { setIsNewMessage } = useContext(UIContext);

  // Compute the message type once based on role; avoids recreating the listener unnecessarily
  const userMessage = useMemo(() => {
    const role = ACM[typeOfUser];
    if (!role) return ['doctorMessage'];
    if (role.isDoctor) return ['patientMessage'];
    if (role.isTeacher) return ['studentMessage', 'teacherMessage']; // Teachers receive from both students and other teachers
    if (role.isStudent) return ['teacherMessage']; // Students receive messages from teachers
    if (role.isPatient) return ['doctorMessage'];
    return ['doctorMessage'];
  }, [typeOfUser]);

  // Stable unread counts subscription — only depends on uid and computed userMessage
  useEffect(() => {
    if (!user?.uid) return;
    
    // Fetch ALL messages - filter in JavaScript to avoid Firestore index
    const messagesRef = collection(FirestoreDB, 'notifications', user?.uid, 'messages');
    
    const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
      const tempCounts = {};
      
      // Filter manually in JavaScript
      snapshot.forEach((item) => {
        const data = item.data();
        // Check if notificationSource matches any of the userMessage types (now supports arrays)
        const matches = Array.isArray(userMessage) 
          ? userMessage.includes(data.notificationSource)
          : data.notificationSource === userMessage;
          
        if (data.readStatus === false && matches) {
          const { userId } = data;
          tempCounts[userId] = (tempCounts[userId] || 0) + 1;
        }
      });

      setIsNewMessage(Object.keys(tempCounts)?.length > 0);
      setUnreadCounts2(tempCounts);
    });

    return () => unsubscribe();
  }, [user?.uid, userMessage, setIsNewMessage, setUnreadCounts2]);

  // One-time mark-as-read when the selected chat changes (no live listener)
  useEffect(() => {
    if (!selectedChat || !user?.uid) return;

    const run = async () => {
      try {
        const userId = ACM[typeOfUser]?.isDoctor
          ? selectedChat.patientId
          : ACM[typeOfUser]?.isTeacher
          ? selectedChat.id
          : ACM[typeOfUser]?.isStudent
          ? selectedChat.id
          : selectedChat.doctorId;

        if (!userId) return;

        // Fetch ALL messages - filter in JavaScript to avoid Firestore index
        const messagesRef = collection(FirestoreDB, 'notifications', user?.uid, 'messages');
        const snapshot = await getDocs(messagesRef);
        
        const updates = [];
        snapshot.forEach((docSnapshot) => {
          const messageData = docSnapshot.data();
          // Filter manually in JavaScript - check both userId and notificationSource
          const matchesNotificationSource = Array.isArray(userMessage)
            ? userMessage.includes(messageData.notificationSource)
            : messageData.notificationSource === userMessage;
          
          if (messageData.readStatus === false && messageData.userId === userId && matchesNotificationSource) {
            const messageRef = doc(
              FirestoreDB,
              'notifications',
              user?.uid,
              'messages',
              docSnapshot.id
            );
            updates.push(updateDoc(messageRef, { readStatus: true }));
          }
        });
        
        if (updates.length > 0) {
          await Promise.all(updates);
          console.log(`Marked ${updates.length} messages as read`);
        }

        // Reset the unread count for this chat to zero in local state
        setUnreadCounts((prevCounts) =>
          prevCounts.map((countObj) => {
            // Check all possible userId fields
            const matches =
              countObj.patientId === userId ||
              countObj.studentId === userId ||
              countObj.teacherId === userId ||
              countObj.doctorId === userId ||
              countObj.id === userId;
            
            return matches ? { ...countObj, count: 0 } : countObj;
          })
        );
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    };

    run();
  }, [selectedChat, user?.uid, typeOfUser, setUnreadCounts]);
  return null;
};

export default UnreadMessageCounts;
